<template>
  <div
    v-if="isVisible"
    class="layer-host"
    :data-layer-depth="layerDepth"
    :style="rootStyle">

    <!-- Per-layer mask scoped to the parent layer's exposed peek strip.
         Each LayerHost owns its own mask sized to the band of parent
         visible to the LEFT of this drawer body — so dimming doesn't
         stack viewport-wide across ancestors. The mask is its own click
         target: clicking it pushes this layer's parent path. Cascade
         multi-layer jumps work for free — pushing /utility/623 from L4
         automatically closes L2, L3, L4 because their routes no longer
         match the new URL. -->
    <div
      class="layer-host__mask"
      :style="maskStyle"
      aria-hidden="true"
      @click.stop="onMaskClick"></div>

    <transition name="layer-host-slide" appear>
      <aside
        class="layer-host__drawer"
        role="dialog"
        aria-modal="true"
        :aria-label="ariaLabel || ('Layer ' + layerDepth)"
        :style="bodyStyle">

        <button
          type="button"
          class="layer-host__close"
          aria-label="Close"
          @click="beforeClose">
          <i class="el-icon-close"></i>
        </button>

        <div
          v-if="layerDepth >= 2 && breadcrumb.length"
          class="layer-host__breadcrumb"
          aria-label="Layer breadcrumb">
          <span
            v-for="(segment, i) in breadcrumb"
            :key="segment.depth">
            <a
              href="#"
              @click.prevent="onBreadcrumbClick(segment)">{{ segment.label }}</a>
            <span v-if="i < breadcrumb.length - 1" class="layer-host__breadcrumb-sep"> › </span>
          </span>
        </div>

        <slot><router-view /></slot>

        <div class="layer-host__live-region" role="status" aria-live="polite" aria-atomic="true"></div>

      </aside>
    </transition>

  </div>
</template>

<script>
import { PopupManager } from 'element-ui/lib/utils/popup'
import { parseDrawerPath } from '@/lib/drawerParser'
import { extractBasePath } from '@/lib/layerNav'

// Width sequence per cascade-modal spec — outermost widest, deeper layers
// step inwards in 8% increments. Clamped to depth 4; deeper routes use
// router.replace (see lib/layerNav.js pushLayer).
const DESKTOP_WIDTH_PCT = { 1: 92, 2: 84, 3: 76, 4: 68 }
const MOBILE_FULLSCREEN_MQ = new Set(['xs', 'sm', 'md'])

// Module-level flag: set to true when a dirty-discard confirmation has been
// acknowledged for the current navigation. Reset after the navigation settles.
// This prevents the L1 guard from re-confirming when a cascade-close of multiple
// dirty layers already prompted via L2's guard first.
// TODO(XOL-243): multi-dirty cascade may double-prompt if both L1 and L2 are
// dirty at the same time — a single shared flag here partially mitigates it
// but the scenario is rare (two stacked forms both mid-edit). A more robust
// solution would require a shared navigation-scoped context.
let _sharedDirtyConfirmed = false

// Reset _sharedDirtyConfirmed via a SINGLE module-level router.afterEach (added
// once, on the first LayerHost mount). The per-instance $route watcher used to be
// the only reset, but a discard-close DESTROYS the confirming LayerHost as part of
// the same navigation, so its watcher could be torn down before it fired — leaking
// the flag `true` and suppressing the dirty prompt on the NEXT drawer that opens
// (XOL-231). afterEach fires after every navigation completes, independent of any
// component's lifecycle, so the flag is always cleared once a confirmed close lands.
let _afterEachRegistered = false

// Body-scroll lock: when any LayerHost becomes visible, freeze the page
// behind it so wheel/touch scroll on the mask doesn't move the underlying
// content. Restore the original overflow when the last visible layer closes.
// Module-level so nested layers share one counter — L1+L2 visible should
// still leave a single lock, not two competing toggles.
let _visibleLayerCount = 0
let _originalBodyOverflow = null
function _lockBodyScroll () {
  if (_visibleLayerCount === 0) {
    _originalBodyOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
  }
  _visibleLayerCount += 1
}
function _unlockBodyScroll () {
  _visibleLayerCount = Math.max(0, _visibleLayerCount - 1)
  if (_visibleLayerCount === 0) {
    document.body.style.overflow = _originalBodyOverflow || ''
    _originalBodyOverflow = null
  }
}
// Light per-layer dim — matches the spec's 0.18 (mocked in
// docs/UX-architecture/mocks/XOL-209-cascade-modal-demo.html as a v-modal
// override). Low opacity keeps the cascade legible when 4 layers stack.
// Actual opacity values live in the .layer-host__mask CSS rule below so
// the :hover state can darken without inline styles fighting it.

export default {
  name: 'layer-host',

  props: {
    layerDepth: {
      type: Number,
      required: true
    },
    breadcrumb: {
      type: Array,
      default: () => []
    },
    ariaLabel: {
      type: String,
      default: null
    },
    // DrawerStack supplies this to force the layer visible without relying on
    // $route.matched depth. Default false preserves the PR-1 canary behaviour
    // where isVisible is computed from matched length.
    forceVisible: {
      type: Boolean,
      default: false
    },
    // When DrawerStack drives this layer it passes the logical parent location
    // so beforeClose / mask click navigate to the right URL. Default null
    // preserves PR-1 canary behaviour (pathAtDepth fallback used instead).
    parentLocation: {
      type: Object,
      default: null
    },
    // XOL-609: DrawerStack passes the number of layers in the stack. Every DrawerStack layer
    // is force-visible, so without it each one would count as topmost and own Esc: one press
    // would close the whole stack. Default null keeps today's value (layerDepth) for a
    // force-visible layer that is not driven by a stack.
    stackDepth: {
      type: Number,
      default: null
    }
  },

  data () {
    return {
      cascadeBusy: false,
      listenerAttached: false,
      dirtyCount: 0
    }
  },

  computed: {
    isVisible () {
      // When DrawerStack owns this layer it forces visibility — the URL-matched
      // depth check doesn't apply because DrawerStack manages visibility itself.
      if (this.forceVisible) return true
      // Visible iff the route still has a matched segment at or below this depth.
      return this.$route.matched.length > this.parentDepth
    },

    parentDepth () {
      return this.layerDepth - 1
    },

    currentDepth () {
      // When DrawerStack drives this layer the matched-record depth counter
      // doesn't reflect the virtual cascade. It passes the stack's size as
      // stackDepth, so isTopmost is true only for the last layer. Without it,
      // fall back to layerDepth (every force-visible layer is topmost).
      if (this.forceVisible) return this.stackDepth != null ? this.stackDepth : this.layerDepth
      return this.$route.matched.filter(r => r.meta && r.meta.layerDepth).length
    },

    isTopmost () {
      return this.layerDepth === this.currentDepth
    },

    isMobile () {
      return MOBILE_FULLSCREEN_MQ.has(this.$mq)
    },

    bodyWidthPct () {
      if (this.isMobile) return 100
      const depth = Math.min(this.layerDepth, 4)
      return DESKTOP_WIDTH_PCT[depth] || 68
    },

    parentBodyWidthPct () {
      // For L1 the parent is the base page (full viewport). For L2+ it's
      // the previous layer's body width.
      if (this.layerDepth === 1) return 100
      if (this.isMobile) return 100
      const depth = Math.min(this.layerDepth - 1, 4)
      return DESKTOP_WIDTH_PCT[depth] || 100
    },

    drawerZIndex () {
      // 3000 base + 10/depth: room for body (z+1) above mask (z) per layer.
      return 3000 + this.layerDepth * 10
    },

    maskLeftPct () {
      // Mask starts at the parent body's LEFT edge.
      return 100 - this.parentBodyWidthPct
    },

    maskWidthPct () {
      // Mask spans from the parent's left edge to this body's left edge —
      // i.e. exactly the strip of the parent exposed to the left of this
      // drawer. (E.g. L1's mask is the 8% page strip; L2's mask is the 8%
      // L1 peek; etc.)
      return this.parentBodyWidthPct - this.bodyWidthPct
    },

    rootStyle () {
      // Pin the wrapper to the viewport so child percent units resolve
      // against the viewport. Itself transparent — only mask + body paint.
      return {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        pointerEvents: 'none',
        zIndex: this.drawerZIndex
      }
    },

    maskStyle () {
      // Background/cursor/transition come from the .layer-host__mask CSS rule
      // so the :hover selector can darken on mouse-over (inline styles would
      // block the :hover state).
      return {
        position: 'fixed',
        top: 0,
        bottom: 0,
        left: this.maskLeftPct + '%',
        width: this.maskWidthPct + '%',
        pointerEvents: 'auto',
        zIndex: this.drawerZIndex
      }
    },

    bodyStyle () {
      return {
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        width: this.bodyWidthPct + '%',
        background: '#fff',
        boxShadow: '-8px 0 16px rgba(0, 0, 0, 0.10)',
        pointerEvents: 'auto',
        zIndex: this.drawerZIndex + 1,
        overflowY: 'auto',
        // Breathing room above slot content so the consumer's first row
        // (filter-bar title, etc.) doesn't crowd the absolute-positioned
        // X close button at top: 12px.
        paddingTop: '40px'
      }
    },

    computedParentLocation () {
      // Prop wins (DrawerStack supplies it); else fall back to the route-matched
      // path resolution for the PR-1 canary.
      if (this.parentLocation) return this.parentLocation
      return { path: this.pathAtDepth(this.parentDepth) }
    },

    hasDirty () {
      return this.dirtyCount > 0
    }
  },

  watch: {
    // Not immediate (XOL-609): an immediate watcher runs inside initState, before created()
    // assigns _boundKeyDown, so it attached `undefined` (a no-op) and flagged the listener as
    // attached. mounted() makes the first attach; this handles later flips.
    isTopmost (now) {
      this.syncListener(now)
    },
    isVisible: {
      handler (now, was) {
        // Body-scroll lock follows visibility, not mount. The L1 LayerHost
        // stays mounted while a child L2 is open; both contribute one lock
        // each so the underlying page stays frozen until everything closes.
        if (now && !was) _lockBodyScroll()
        else if (!now && was) _unlockBodyScroll()
      },
      immediate: true
    },
    hasDirty (v) {
      if (v) {
        window.addEventListener('beforeunload', this._beforeUnload)
      } else {
        window.removeEventListener('beforeunload', this._beforeUnload)
      }
    }
  },

  methods: {
    pathAtDepth (targetDepth) {
      // Resolve the URL for the layer at `targetDepth` against the current
      // matched chain. depth=0 → the L0 base page (the record one slot
      // above the first LayerHost — e.g. /utility); depth=N → the
      // LayerHost record whose meta.layerDepth === N.
      //
      // NOTE: a naive "deepest non-layerDepth record" search hits the index
      // child of the L1 LayerHost (whose path resolves to the L1 path
      // again), not the L0 page. Anchor against the first LayerHost instead.
      const matched = this.$route.matched
      let record = null
      if (targetDepth <= 0) {
        const firstLayerIndex = matched.findIndex(r => r.meta && r.meta.layerDepth === 1)
        if (firstLayerIndex > 0) {
          record = matched[firstLayerIndex - 1]
        } else if (matched.length > 0) {
          record = matched[0]
        }
      } else {
        record = matched.find(r => r.meta && r.meta.layerDepth === targetDepth)
      }
      if (!record || !record.path) return '/'
      const resolved = record.path.replace(
        /:([^/]+?)(\([^)]+\))?(?=\/|$)/g,
        (_, name) => this.$route.params[name] || ''
      )
      return resolved || '/'
    },

    onMaskClick () {
      // Clicking this layer's mask = clicking the parent's visible peek
      // strip. Push the parent path; any deeper layers that were also
      // open close automatically because their routes won't match.
      if (this.cascadeBusy) return
      this.beforeClose()
    },

    beforeClose () {
      // Forward-only close: push the parent path. Browser back returns the
      // user to the deeper route they closed out of.
      this.$router.push(this.computedParentLocation)
    },

    onBreadcrumbClick (segment) {
      this.$router.push({ path: this.pathAtDepth(segment.depth) })
    },

    // CAPTURE phase, record only (XOL-609). Does this Esc belong to something above or inside
    // the drawer that closes itself on Esc without stopping it?
    //   1  an Element modal is open (a $confirm / $msgbox, an el-dialog, an el-drawer such as
    //      Configure Emails). Element's own Esc handler is a bubble listener on window,
    //      registered at boot, so it always runs BEFORE onKeyDown and has already popped a
    //      $confirm off PopupManager's modalStack by then (an el-dialog / el-drawer closes a
    //      tick later, but nothing here looks). Capture runs before every bubble listener, so
    //      the stack is still intact. Every PopupManager z-index is >= 4000, above the drawers
    //      (3010-3051): anything in the stack is above this drawer.
    //   2  an OPEN el-cascader. Its Esc closes the dropdown but does not stop the event
    //      (cascader.vue handleKeyDown), unlike el-select and the pickers. The open state is
    //      still in the DOM at capture time: `.el-input.is-focus` is bound only to
    //      dropDownVisible. Two ways in: the target is inside the cascader (it closes itself,
    //      _escOwnedByCascader), or focus is elsewhere, typically on body after the arrow icon
    //      was clicked, and the cascader's own keydown handler never sees the key. This layer
    //      then finds the open one in its own tree (_openCascaderInLayer) and closes it at the
    //      bubble, in onKeyDown.
    // This only records the event; the close stays in onKeyDown (bubble) so an el-select /
    // picker that stops Esc at its target still keeps the drawer. Every Esc overwrites the
    // record, so a stale event (ApprovalLockDialog stops the bubble) is never kept.
    onKeyDownCapture (event) {
      const isEsc = event.key === 'Escape' || event.key === 'Esc' || event.keyCode === 27
      if (!isEsc) return
      this._escCascader = null
      let taken = (PopupManager.modalStack && PopupManager.modalStack.length > 0) ||
        this._escOwnedByCascader(event.target)
      if (!taken) {
        this._escCascader = this._openCascaderInLayer()
        taken = !!this._escCascader
      }
      this._escUnderPopup = taken ? event : null
    },

    // Rule 2, second way in: an open cascader inside this layer while the Esc target is not in
    // it. Returns the el-cascader instance (the root element's __vue__), or null. Only the
    // trigger is inside $el: the dropdown panel is appended to body (Popper appendToBody), and
    // a key pressed inside the panel is already caught by _escOwnedByCascader.
    _openCascaderInLayer () {
      const root = this.$el
      if (!root || typeof root.querySelectorAll !== 'function') return null
      const focused = root.querySelectorAll('.el-cascader .el-input.is-focus')
      for (let i = 0; i < focused.length; i++) {
        const el = focused[i].closest('.el-cascader')
        const vm = el && el.__vue__
        if (vm && typeof vm.toggleDropDownVisible === 'function') return vm
      }
      return null
    },

    // Rule 2 of the Esc precedence: the target is inside an open cascader (its input, or its
    // dropdown panel, which Element appends to body).
    _escOwnedByCascader (target) {
      if (!target || typeof target.closest !== 'function') return false
      if (target.closest('.el-cascader__dropdown')) return true
      const cascader = target.closest('.el-cascader')
      return !!(cascader && cascader.querySelector('.el-input.is-focus'))
    },

    // Esc claims (XOL-609): an overlay inside the drawer that closes itself on Esc and wants
    // the key before the drawer sees it (FieldCue's popover). Newest first; the first claim
    // that returns true consumes the key.
    _claimEsc () {
      const claims = this._escClaims.slice()
      for (let i = claims.length - 1; i >= 0; i--) {
        if (claims[i]() === true) return true
      }
      return false
    },

    // Esc runs the X's close (a push of the parent location, so every guard in the router
    // hook runs) plus what a mouse click on the X does by itself:
    //   - the click moves focus first, which fires the native `change` of a focused input
    //     (TextInput commits on `change` only), so blur before pushing;
    //   - and when a guard refuses (busy veto, the dirty confirm, a leave guard's Stay) focus
    //     goes back to where the user was typing. vue-router 2.8.1 calls onAbort for next(false).
    // beforeClose() is untouched, so the X and the mask keep their call shape.
    closeFromKeyboard () {
      let active = document.activeElement
      if (active && active !== document.body && typeof active.blur === 'function') {
        active.blur()
      } else {
        active = null
      }

      const onAbort = function () {
        if (!active || !document.body.contains(active)) return
        if (document.activeElement !== document.body) return
        if (PopupManager.modalStack && PopupManager.modalStack.length > 0) return
        active.focus()
      }

      this.$router.push(this.computedParentLocation, undefined, onAbort)
    },

    onKeyDown (event) {
      // Esc closes exactly one layer, the topmost, through the same push as the X button
      // (browser back restores). Accept both 'Escape' (modern) and 'Esc' (IE/older Edge) for
      // KeyboardEvent.key. The precedence, first match wins (drawer-invariants.md I5):
      //   1, 2  an Element modal or an open cascader owns it (recorded at capture)
      //   3     a held key is one press
      //   4     an IME composition owns it
      //   5     a control at the target already handled it (preventDefault): quill's
      //         toolbar pickers, el-select, the approval-lock dialog
      //   6     not the top layer, a cascade/confirm/leave-guard already in progress
      //   7     an overlay inside the drawer claims it (FieldCue)
      //   8     close
      const isEsc = event.key === 'Escape' || event.key === 'Esc'
      if (!isEsc) return

      // XOL-609: this Esc belonged to a modal open above the drawer (or an open cascader),
      // recorded in the capture phase. It was theirs: the drawer neither closes nor has its
      // default prevented. Compared by identity, so the next Esc is never affected.
      if (event === this._escUnderPopup) {
        this._escUnderPopup = null
        // An open cascader the key did not reach (focus on body): close its dropdown here.
        const cascader = this._escCascader
        this._escCascader = null
        if (cascader) cascader.toggleDropDownVisible(false)
        return
      }

      // A held Esc counts as one press, so it can't run through the modal and then the
      // drawer (and any drawer under it) one after another.
      if (event.repeat) return

      // IME: Esc cancels the composition, not the drawer.
      if (event.isComposing || event.keyCode === 229) return

      // A control at the target handled it already.
      if (event.defaultPrevented) return

      // _confirmOpen covers the tick between $confirm and the box entering the modal stack;
      // _leaveGuardRun the flush before a leave guard's dialog appears: a second Esc there
      // would only issue a second navigation that supersedes the first.
      if (!this.isTopmost || this.cascadeBusy || this._confirmOpen || this._leaveGuardRun) return

      if (this._claimEsc()) {
        event.preventDefault()
        return
      }

      event.preventDefault()
      this.closeFromKeyboard()
    },

    isDrawerDirty () {
      // Fresh read at close time — checks every registered dirty-checker fn.
      return Array.from(this._dirtyCheckers).some(function (fn) { return fn() })
    },

    isDrawerBusy () {
      // Fresh read at close time — true if any registered form is mid-save.
      return Array.from(this._busyCheckers).some(function (fn) { return fn() })
    },

    _isLeavingThisLayer (to) {
      // Returns true when the navigation would CLOSE this layer (i.e. this
      // layer's entry is no longer in the destination drawer stack at its
      // depth index). Returns false when navigating within or deeper into this
      // layer (tab switch, drill into child, etc.).
      //
      // Strategy: derive the base path from parentLocation (supplied by
      // DrawerStack for forceVisible layers) or fall back to stripping the
      // drawer tail from the current route. Then parse the destination path's
      // drawer tail and check whether this layer's entry is still present.
      //
      // This replaces the old matched-depth heuristic, which was always 0
      // because no routes in production register meta.layerDepth.

      if (this.forceVisible) {
        // DrawerStack-driven path: parentLocation is always supplied.
        // parentLocation.path for L1 is the DrawerStack basePath itself
        // (e.g. '/sites'). For L2+ it contains the parent drawer segment
        // (e.g. '/sites/d.site.5'). In both cases, stripping any '/d.' tail
        // via extractBasePath() recovers the true DrawerStack basePath —
        // the stable prefix common to all entries in this stack.
        var parentLoc = this.computedParentLocation
        var parentPath = parentLoc ? (parentLoc.path || String(parentLoc)) : null

        if (!parentPath) {
          // Defensive fallback — treat as leaving if we can't determine basePath.
          return true
        }

        // Recover the DrawerStack's basePath (e.g. '/sites') regardless of
        // whether parentPath already contains drawer segments.
        var stackBasePath = extractBasePath(parentPath)

        // Derive the drawer tail for the destination path.
        var toPath = to.path || ''
        var toTail = ''
        if (toPath.startsWith(stackBasePath)) {
          toTail = toPath.slice(stackBasePath.length)
        } else {
          // Destination is outside our DrawerStack's base entirely — leaving.
          return true
        }
        if (toTail.startsWith('/')) toTail = toTail.slice(1)

        var toStack = parseDrawerPath(toTail)

        // Determine this layer's own entry by parsing the CURRENT route path's
        // drawer tail relative to the same DrawerStack basePath.
        // layerDepth is 1-indexed; stack array is 0-indexed.
        var myDepthIdx = this.layerDepth - 1
        var currentTail = ''
        var currentPath = this.$route.path || ''
        if (currentPath.startsWith(stackBasePath)) {
          currentTail = currentPath.slice(stackBasePath.length)
        }
        if (currentTail.startsWith('/')) currentTail = currentTail.slice(1)
        var currentStack = parseDrawerPath(currentTail)
        var myEntry = currentStack[myDepthIdx] || null

        if (!myEntry) {
          // Can't identify our own entry — treat as leaving (safe default).
          return true
        }

        // STAYING: destination stack has an entry at our depth with the same
        // name and id. Tab/subtab differences are intentionally ignored so
        // that a tab switch on the same entry does NOT trigger a dirty prompt.
        if (
          toStack.length > myDepthIdx &&
          toStack[myDepthIdx].name === myEntry.name &&
          String(toStack[myDepthIdx].args.id) === String(myEntry.args.id)
        ) {
          return false
        }

        // Entry absent or changed — this layer is closing.
        return true
      }

      // Legacy / canary path (no forceVisible): fall back to matched-depth
      // heuristic for route-configured LayerHosts.
      var toDepth = (to.matched || []).filter(function (r) { return r.meta && r.meta.layerDepth }).length
      return toDepth < this.layerDepth
    },

    // XOL-596: the leave guards that have something to do for this navigation.
    _guardsNeeded (to) {
      var out = []

      this._leaveGuards.forEach(function (needed, fn) {
        if (!needed || needed(to)) {
          out.push(fn)
        }
      })

      return out
    },

    // XOL-596: run the guards once; a second close while they run waits for the
    // same answer (one dialog, not two). Resolves true when every guard agrees.
    //
    // The navigation that called this is HELD: its `next` is called only when the
    // answer arrives. That cannot wedge the router. In vue-router 2.8.1 a
    // navigation that a newer one supersedes (`this.pending !== route`) aborts the
    // moment it reaches its next hook or its end, so a late `next()` of the
    // superseded one is a no-op, and the newer navigation runs its own guards.
    _awaitLeaveGuards (to, guards) {
      var self = this

      if (!this._leaveGuardRun) {
        this._leaveGuardRun = Promise.all(guards.map(function (fn) {
          try {
            return fn(to)
          } catch (e) {
            return Promise.reject(e)
          }
        })).then(function (answers) {
          self._leaveGuardRun = null
          return answers.every(function (a) { return a !== false })
        }, function () {
          self._leaveGuardRun = null
          return false
        })
      }

      return this._leaveGuardRun
    },

    syncListener (shouldBeAttached) {
      // ESC keydown is the only window-level listener (a bubble one that closes, and a
      // capture one that records an Esc meant for an Element modal or an open cascader,
      // XOL-609) — cascade pop runs via per-mask @click bindings now, no global mousedown
      // hit-test. First attached from mounted(), when _boundKeyDown exists.
      if (shouldBeAttached && !this.listenerAttached) {
        window.addEventListener('keydown', this._boundKeyDown)
        window.addEventListener('keydown', this._boundKeyDownCapture, true)
        this.listenerAttached = true
      } else if (!shouldBeAttached && this.listenerAttached) {
        window.removeEventListener('keydown', this._boundKeyDown)
        window.removeEventListener('keydown', this._boundKeyDownCapture, true)
        this._escUnderPopup = null
        this._escCascader = null
        this.listenerAttached = false
      }
    }
  },

  provide () {
    // provide() runs before created() in Vue 2, so we build all the dirty-
    // guard non-reactive state here and stash it on `this` for later use
    // by mounted() and beforeDestroy().
    this._confirmOpen = false
    this._escUnderPopup = null
    this._escCascader = null
    this._dirtyConfirmed = false
    this._busyNoticeShown = false
    this._dirtyCheckers = new Set()
    // Busy checkers: `() => bool` from drawer forms that are mid-save (autosave
    // in flight). While any returns true, the close is BLOCKED (not prompted) —
    // closing during a save would race the in-flight request.
    this._busyCheckers = new Set()
    // Leave guards (XOL-596): a drawer form that must save before the drawer
    // closes (the request workflow's pause-flush) registers `run(to) =>
    // Promise<boolean>` and, optionally, a SYNCHRONOUS `needed(to) => boolean`.
    // Unlike busy, a guard AWAITS: while it runs the navigation is simply held
    // (`next` is called when it answers, which vue-router allows), so the history
    // entry is made once and a replace stays a replace. A false keeps the drawer
    // open (the guard has already shown why). A guard whose `needed` says no is
    // not run, and the close goes straight through in the same tick.
    this._leaveGuards = new Map()
    this._leaveGuardRun = null
    // Esc claims (XOL-609): `() => boolean` from an overlay inside the drawer (FieldCue's
    // popover) that closes itself on Esc. Newest first; returning true consumes the key.
    this._escClaims = []
    this._beforeUnload = function (e) {
      e.preventDefault()
      e.returnValue = ''
    }

    var self = this
    this._drawerDirtyRegistry = {
      register: function (fn) {
        self._dirtyCheckers.add(fn)
      },
      unregister: function (fn) {
        self._dirtyCheckers.delete(fn)
      },
      notifyDirtyChange: function (isDirty) {
        if (isDirty) {
          self.dirtyCount += 1
        } else {
          self.dirtyCount = Math.max(0, self.dirtyCount - 1)
        }
      },
      registerBusy: function (fn) {
        self._busyCheckers.add(fn)
      },
      unregisterBusy: function (fn) {
        self._busyCheckers.delete(fn)
      },
      registerLeaveGuard: function (fn, needed) {
        self._leaveGuards.set(fn, typeof needed === 'function' ? needed : null)
      },
      unregisterLeaveGuard: function (fn) {
        self._leaveGuards.delete(fn)
      },
      registerEscClaim: function (fn) {
        if (self._escClaims.indexOf(fn) === -1) self._escClaims.push(fn)
      },
      unregisterEscClaim: function (fn) {
        var i = self._escClaims.indexOf(fn)
        if (i !== -1) self._escClaims.splice(i, 1)
      }
    }

    return {
      drawerDirtyRegistry: this._drawerDirtyRegistry
    }
  },

  created () {
    this._boundKeyDown = this.onKeyDown.bind(this)
    this._boundKeyDownCapture = this.onKeyDownCapture.bind(this)
    // The element that had focus when this layer opened (the chevron or row link that pushed
    // it, or a link inside the parent layer): focus goes back to it when the layer closes.
    // None for a drawer opened by deep link or reload.
    var active = document.activeElement
    this._opener = active && active !== document.body ? active : null
  },

  mounted () {
    // Clear the cascade discard-confirmation flag after every navigation settles,
    // regardless of which LayerHosts were destroyed by that navigation. Registered
    // once for the whole app (not per-instance) so a discard-close can't leak the
    // flag and suppress the next drawer's dirty prompt (XOL-231).
    if (!_afterEachRegistered && this.$router && typeof this.$router.afterEach === 'function') {
      _afterEachRegistered = true
      this.$router.afterEach(function () { _sharedDirtyConfirmed = false })
    }

    // Combined beforeEach guard:
    // 1. cascadeBusy — suppress overlapping cascade-pop clicks while a route
    //    transition is in flight.
    // 2. dirty guard — when navigation would close THIS layer and unsaved
    //    changes are present, veto and prompt.
    var self = this
    this._routerBefore = this.$router.beforeEach(function (to, from, next) {
      // Block closing this layer while a form inside it is mid-save (autosave
      // in flight). No discard prompt — saves are brief; just veto and tell the
      // user to wait, so the close can't race the in-flight request.
      if (self.isVisible && self._isLeavingThisLayer(to) && self.isDrawerBusy()) {
        next(false)
        if (!self._busyNoticeShown) {
          self._busyNoticeShown = true
          self.$message({ message: 'Please wait — still saving…', type: 'warning' })
          setTimeout(function () { self._busyNoticeShown = false }, 1500)
        }
        return
      }

      // Everything after the leave guards: the dirty-discard prompt of the
      // other drawers, then the navigation.
      var proceed = function () {
        // If a dirty-discard was already confirmed (either by this layer's own
        // one-shot flag or by the module-level shared flag set by another layer
        // for the same navigation), let this navigation through without a second
        // prompt. Reset the per-instance one-shot flag here; the shared flag is
        // reset by the $route watcher once the route settles.
        if (self._dirtyConfirmed || _sharedDirtyConfirmed) {
          self._dirtyConfirmed = false
          self.cascadeBusy = true
          next()
          return
        }

        // Only intercept when this layer is visible and the nav would close it.
        if (self.isVisible && self._isLeavingThisLayer(to) && self.isDrawerDirty()) {
          // Veto the navigation first.
          next(false)

          // Show the confirm dialog. Track _confirmOpen to prevent Esc from
          // simultaneously closing the drawer (a11y guard in onKeyDown).
          self._confirmOpen = true
          self.$confirm(
            'You have unsaved changes. If you leave now, your edits will be lost.',
            'Discard unsaved changes?',
            {
              confirmButtonText: 'Discard changes',
              cancelButtonText: 'Keep editing',
              type: 'warning'
            }
          ).then(function () {
            self._confirmOpen = false
            // User chose to discard — set both the per-instance one-shot flag
            // and the shared module-level flag so sibling LayerHosts for the
            // same navigation don't re-prompt.
            self._dirtyConfirmed = true
            _sharedDirtyConfirmed = true
            self.$router.push(to.fullPath)
          }).catch(function () {
            // User chose to keep editing — stay put.
            self._confirmOpen = false
          })
          return
        }

        self.cascadeBusy = true
        next()
      }

      // XOL-596: a drawer that saves on close (the request workflow) gets to
      // flush and wait first, but only when it says something is pending: with
      // nothing to save the close is not touched at all. When it runs, the
      // navigation is HELD (not vetoed and re-issued: a veto makes vue-router
      // push the old URL back on a Back press and the re-issue pushes again, a
      // stray history entry on every close) and continues, as the same
      // navigation, once every guard has answered.
      if (self.isVisible && self._leaveGuards.size > 0 && self._isLeavingThisLayer(to)) {
        var needed = self._guardsNeeded(to)

        if (needed.length > 0 || self._leaveGuardRun) {
          self._awaitLeaveGuards(to, needed).then(function (ok) {
            if (ok) {
              proceed()
            } else {
              next(false)
            }
          })

          return
        }
      }

      proceed()
    })
    this._afterEachTimeout = null
    this.$watch('$route', function () {
      // Route settled — clear the shared dirty-confirmed flag so it doesn't
      // suppress guards on subsequent independent navigations.
      _sharedDirtyConfirmed = false
      clearTimeout(self._afterEachTimeout)
      self._afterEachTimeout = setTimeout(function () {
        self.cascadeBusy = false
      }, 250)
    })

    this.syncListener(this.isTopmost)
  },

  beforeDestroy () {
    this.syncListener(false)
    // If this layer is being torn down while still visible (route swap mid-
    // open, hot reload, etc.) the isVisible watcher won't fire — release the
    // lock here so we don't leak `body { overflow: hidden }`.
    if (this.isVisible) _unlockBodyScroll()
    if (this._routerBefore) {
      this._routerBefore()
    }
    clearTimeout(this._afterEachTimeout)
    // Always remove the beforeunload listener — don't leak it if the
    // component is torn down while hasDirty is true.
    window.removeEventListener('beforeunload', this._beforeUnload)

    // Return focus to the opener, after the patch that removes this layer has run. Only when
    // focus was dropped (it was inside the removed drawer, or blurred by closeFromKeyboard):
    // never steal focus the user moved on purpose. When several layers close at once, a
    // deeper layer's opener sits inside a removed layer and is skipped. And when a drawer is
    // still open and the opener is not inside any of them, this layer was replaced in place
    // (pushDrawer swaps a same-name entry: a link in site.5 opens site.6): the opener is on the
    // page behind the new aria-modal drawer, and focus belongs to the new one.
    var opener = this._opener
    this._opener = null
    if (opener) {
      this.$nextTick(function () {
        if (!document.body.contains(opener)) return
        var now = document.activeElement
        if (now && now !== document.body) return
        var drawers = document.querySelectorAll('.layer-host__drawer')
        if (drawers.length > 0 && !Array.prototype.some.call(drawers, function (d) { return d.contains(opener) })) return
        if (typeof opener.focus === 'function') opener.focus()
      })
    }
  }
}
</script>

<style scoped>
/* Per-layer mask. Light dim by default; hover advertises the click target
   per the cascade-modal mock's `.peek-hover-zone` design. Position, sizing,
   and z-index are applied inline via :style — only paint properties live
   here so :hover can take effect. */
.layer-host__mask {
  background: rgba(0, 0, 0, 0.18);
  cursor: pointer;
  transition: background 120ms ease;
}

.layer-host__mask:hover {
  background: rgba(0, 0, 0, 0.28);
}

/* Close button — floats over the drawer content, top-right corner. */
.layer-host__close {
  position: absolute;
  top: 12px;
  right: 16px;
  background: transparent;
  border: 0;
  padding: 6px;
  font-size: 22px;
  line-height: 1;
  color: #909399;
  cursor: pointer;
  border-radius: 4px;
  z-index: 1;
}

.layer-host__close:hover {
  color: #303133;
  background: #f5f7fa;
}

.layer-host__breadcrumb {
  font-size: 12px;
  color: #909399;
  padding: 14px 56px 0 20px;
}

.layer-host__breadcrumb a {
  color: #409eff;
  text-decoration: none;
  cursor: pointer;
}

.layer-host__breadcrumb a:hover {
  text-decoration: underline;
}

.layer-host__breadcrumb-sep {
  margin: 0 4px;
  color: #c0c4cc;
}

.layer-host__live-region {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
}

/* Slide-from-right open transition */
.layer-host-slide-enter-active,
.layer-host-slide-leave-active {
  transition: transform 0.3s ease;
}

.layer-host-slide-enter,
.layer-host-slide-leave-to {
  transform: translateX(100%);
}
</style>
